/**
 * TrialBanner
 *
 * Slim dismissible banner shown at the top of the app during a free trial
 * or after a trial has expired. Dismissed state is stored in sessionStorage
 * and cleared on page reload.
 */

import React, { useState } from 'react';
import { ArrowUpRight, X, Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { getBillingStatus, createCheckoutSession } from '../api/billing';
import type { BillingStatus } from '../api/billing';

const DISMISSED_KEY = 'danny_trial_banner_dismissed';

function useBillingStatus() {
  return useQuery<BillingStatus>({
    queryKey: ['billing', 'status'],
    queryFn: getBillingStatus,
    // Refresh every 10 minutes — no need to hammer the API
    staleTime: 10 * 60 * 1000,
  });
}

export function TrialBanner(): React.ReactElement | null {
  const { data: billing } = useBillingStatus();
  const [dismissed, setDismissed] = useState<boolean>(
    () => sessionStorage.getItem(DISMISSED_KEY) === 'true'
  );
  const [upgrading, setUpgrading] = useState(false);

  if (!billing || dismissed) return null;

  const isTrialActive = billing.trialActive === true && billing.trialDaysLeft != null;
  const isTrialExpired =
    billing.subscriptionTier === 'free' && billing.trialActive === false;

  // Paid subscriber — no banner
  if (!isTrialActive && !isTrialExpired) return null;

  // Suppress during the trial honeymoon (first ~4 days of a 14-day trial).
  // Showing "14 days left" on session zero immediately after signup feels
  // commercial and premature — the user has barely seen Danny work. The
  // banner kicks in once trialDaysLeft <= 10, giving the user time to
  // discover value before being pitched.
  if (isTrialActive && (billing.trialDaysLeft ?? 0) > 10) return null;

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISSED_KEY, 'true');
    setDismissed(true);
  };

  const handleUpgrade = async () => {
    if (upgrading) return;

    if (!billing.stripeConfigured) {
      window.location.href = '/settings';
      return;
    }

    setUpgrading(true);
    try {
      const { url } = await createCheckoutSession();
      window.location.href = url;
    } catch {
      setUpgrading(false);
    }
  };

  const bannerBg = isTrialActive ? 'bg-amber-500 text-white' : 'bg-orange-600 text-white';

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-40 hidden md:flex items-center justify-between px-4 py-2 text-sm font-medium ${bannerBg}`}
      role="banner"
    >
      <span>
        {isTrialActive ? (
          <>Free trial · {billing.trialDaysLeft} {billing.trialDaysLeft === 1 ? 'day' : 'days'} left</>
        ) : (
          <>Your trial has ended. Upgrade to keep using AI features.</>
        )}
      </span>

      <div className="flex items-center">
        <button
          onClick={handleUpgrade}
          disabled={upgrading}
          className="ml-2 underline hover:no-underline cursor-pointer bg-transparent border-0 text-white font-semibold flex items-center gap-1 disabled:opacity-70"
        >
          {upgrading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <>
              {isTrialActive ? 'Upgrade' : 'Upgrade now'}
              <ArrowUpRight className="w-3.5 h-3.5" />
            </>
          )}
        </button>

        <button
          onClick={handleDismiss}
          aria-label="Dismiss banner"
          className="ml-4 text-white/80 hover:text-white text-lg leading-none bg-transparent border-0 cursor-pointer"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
